import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, of, merge, defer } from 'rxjs';
import { debounceTime, filter, map, take, tap } from 'rxjs/operators';

import { AbilitiesSelectors } from '@recruitee/abilities-data';
import { Ability } from '@recruitee/abilities-types';
import { Analytics } from '@recruitee/analytics';
import { AsyncJobActions } from '@recruitee/async-job-data';
import { AsyncJobType } from '@recruitee/async-job-types';
import { buildUrlActionTrigger } from '@recruitee/common';
import { SampleDataRemoveContext, SubscriptionService } from '@recruitee/company-logic';
import { I18nService } from '@recruitee/i18n';
import { OfferAnalytics } from '@recruitee/offers';
import { JobsActions, JobsFullActions, OfferScope } from '@recruitee/offers-data';
import { OfferStatus } from '@recruitee/offers-types';
import type { OfferItem } from '@recruitee/offers-types';
import { getJobRouterLink } from '@recruitee/offers-util';
import { OfferSlotsCounterComponent } from '@recruitee/pricing';
import { NotificationsController, WindowRef } from '@recruitee/user-interface';
import { injectUntilDestroyed } from '@recruitee/utils';
import { DataSourceService, ViewEmptyState, ViewFormPublicService } from '@recruitee/views-types';

import { SampleDataService } from '../../../onboarding/sample-data/sample-data.service';
import { JobActionsFacade } from '../../facades/job-actions.facade';

@Injectable()
export class JobsDataSourceService implements DataSourceService<OfferItem> {
  public ongoingFollow = new BehaviorSubject<Record<number, boolean>>({});
  public canManageJobs$ = this.store.select(AbilitiesSelectors.hasOne(Ability.ManageJobs));
  public hasMoreSettings$ = this.canManageJobs$;
  public hasSampleData$ = inject(SampleDataService).hasSampleData$;
  public isSampleDataInProgress$ = inject(SampleDataService).sampleDataInProgress$;
  public viewPageHeaderComponent$ = defer(() =>
    of(this.subscription.hasSlotsLimit() ? OfferSlotsCounterComponent : null),
  );

  public pageCtaVisible$ = this.canManageJobs$;
  public viewFormService = signal<ViewFormPublicService | null>(null);
  public sidebarShowMoreThreshold = Number.MAX_SAFE_INTEGER;

  public refreshData$: Observable<void> = merge(
    this.actions$.pipe(
      ofType(
        JobsActions.followSuccess,
        JobsActions.unfollowSuccess,
        JobsActions.createSuccess,
        JobsActions.updateSuccess,
        JobsActions.duplicateSuccess,
        JobsActions.changeStatusSuccess,
        JobsActions.removeOneSuccess,
      ),
    ),
    this.actions$.pipe(
      ofType(JobsActions.updatePositionsSuccess),
      filter(() => !this.viewFormService()?.getValue().sorting?.sortingId),
    ),
    this.actions$.pipe(
      ofType(AsyncJobActions.finish),
      filter(({ payload }) =>
        [
          AsyncJobType.BulkOffersFollow,
          AsyncJobType.BulkOffersUnfollow,
          AsyncJobType.BulkOffersEnableReferralProgram,
          AsyncJobType.BulkOffersDisableReferralProgram,
          AsyncJobType.BulkOffersEnableEeo,
          AsyncJobType.BulkOffersDisableEeo,
          AsyncJobType.BulkOffersDeleteArchived,
          AsyncJobType.BulkOffersRetrieveArchived,
          AsyncJobType.RemoveSampleData,
          AsyncJobType.GenerateSampleData,
        ].includes(payload.job.type),
      ),
    ),
  ).pipe(
    map(() => void 0),
    debounceTime(10),
  );

  public translations = {
    dataSourceName: 'app.views_jobs.name',
    pageCta: 'app.views_jobs.view_add_job',
  };

  public defaultEmptyState$: Observable<ViewEmptyState> = this.canManageJobs$.pipe(
    map(canManageJobs => ({
      header: this.i18n.translate('app.views_jobs.default_empty_state.header'),
      description: `${this.i18n.translate(
        'app.views_jobs.default_empty_state.description',
      )}<br><a href="${this.i18n.translate(
        'app.support_resources.create_jobs',
      )}" target="_blank">${this.i18n.translate('app.views_jobs.default_empty_state.see_how')}</a>`,
      icon: 'work',
      button: canManageJobs
        ? {
            iconName: 'add',
            label: this.i18n.translate('app.views_jobs.default_empty_state.button_label'),
            action: () => this.onPageCtaClick(),
          }
        : undefined,
    })),
  );

  public filteredEmptyState$: Observable<ViewEmptyState> = of({
    header: this.i18n.translate('app.views_jobs.filtered_empty_state.header'),
    description: this.i18n.translate('app.views_jobs.filtered_empty_state.description'),
    icon: 'search',
  });

  private notificationsCtrl = inject(NotificationsController);
  private router = inject(Router);
  private analytics = inject(Analytics);
  private jobAnalytics = inject(OfferAnalytics, { optional: true });
  private jobActionsFacade = inject(JobActionsFacade);
  private untilDestroyed = injectUntilDestroyed();
  private windowRef = inject(WindowRef);

  private sampleDataService = inject(SampleDataService);

  constructor(
    private i18n: I18nService,
    private actions$: Actions,
    private store: Store,
    private subscription: SubscriptionService,
  ) {}

  public toggleOfferFollow(offer: { id: number; slug: string; followed: boolean }): void {
    if (!offer.followed) {
      this.ongoingFollow.next({
        ...this.ongoingFollow.value,
        [offer.id]: true,
      });
      this.store.dispatch(
        JobsFullActions.follow({ payload: { id: offer.id, scope: OfferScope.Active } }),
      );
    } else {
      this.store.dispatch(
        JobsFullActions.unfollow({ payload: { id: offer.id, scope: OfferScope.Active } }),
      );
    }

    this.actions$
      .pipe(
        ofType(JobsFullActions.followSuccess, JobsFullActions.unfollowSuccess),
        tap(({ payload: { job } }) => {
          const notificationText = job.followed
            ? {
                title: this.i18n.translate('app.offers.follow_success.job.title'),
                message: this.i18n.translate('app.offers.follow_success.job.message'),
              }
            : this.i18n.translate('app.offers.offer_unfollowed');
          this.notificationsCtrl.success(notificationText);
        }),
        take(1),
      )
      .subscribe(() => {
        this.ongoingFollow.next({
          ...this.ongoingFollow.value,
          [offer.id]: false,
        });
      });
  }

  public onRecordClick(offer: OfferItem): void {
    this.analytics.track('[Jobs] Job click', { status: offer.status });
    this.router.navigate(getJobRouterLink(offer));
  }

  public getOfferUrl(offer: OfferItem): string {
    const { origin } = this.windowRef.nativeWindow.location;
    return `${origin}/#/offers/${offer.slug}/pipeline`;
  }

  public onEditClick(offer: OfferItem): void {
    if (this.jobAnalytics?.trackEdit) {
      const status = offer.status === OfferStatus.Published ? 'public' : offer.status;
      this.jobAnalytics.trackEdit(status);
    }

    this.router.navigate(['/offers', offer.slug, 'edit']);
  }

  public onDuplicate(offer: OfferItem) {
    this.jobActionsFacade.duplicate(offer);
  }

  public onArchive(offer: OfferItem) {
    this.jobActionsFacade.archive(offer);
  }

  public onRetrieve(offer: OfferItem) {
    this.jobActionsFacade.retrieve(offer);
  }

  public onDelete(offer: OfferItem) {
    this.jobActionsFacade.delete(offer);
  }

  public onAcquisition(offer: OfferItem): void {
    this.router.navigate(['/offers', offer.slug, 'promote_acquisition']);
  }

  public onPageCtaClick(): void {
    this.router.navigateByUrl(buildUrlActionTrigger('CreateJobOpen', null, true));
  }

  public onRemoveSampleData(): void {
    this.sampleDataService.onRemoveSampleData(SampleDataRemoveContext.Jobs);
  }
}
